/**
 * Shared rich-text sanitization helpers for the settlement statement editors
 * (firm template editor and per-matter narrative editor).
 *
 * `sanitizeEditorHtml` is the core: it reduces arbitrary HTML down to a tiny
 * whitelist (p, strong, em, ul, li, br) so pasted or stored markup can never
 * smuggle through unsupported/unsafe tags, and it cleans up paste debris —
 * literal tag-shaped text and boundary-empty paragraphs — that would
 * otherwise silently diverge from what the user sees in the text box (see
 * sc-46148 for the incident that motivated this).
 */

const ALLOWED_TAGS = new Set(['p', 'strong', 'em', 'ul', 'li', 'br']);
const TAG_ALIASES: Record<string, string> = { b: 'strong', i: 'em' };

// Block-level tags whose own leading/trailing whitespace is never meaningful
// content — unlike whitespace between/around inline tags (e.g. the space in
// "Hello <strong>World</strong>"), which must be preserved.
const BLOCK_TAGS = new Set(['p', 'li']);

// ── Text-content cleanup ──────────────────────────────────────────────────
// Applied to every text node while sanitizing, in this order: drop paste
// debris first, then make anything left over safe for the backend.

/**
 * PDF text extraction sometimes leaves literal tag-shaped noise embedded
 * directly in the copied text — real ASCII "<p>"/"</p>", or (from a paste
 * through an intermediate app) the fullwidth lookalikes "＜p＞"/"＜/p＞". This
 * isn't meaningful narrative content, it's paste debris, so drop it outright
 * rather than leaving any stand-in character behind. Matches either bracket
 * style around a short tag-like name, e.g. "<p>", "</p>", "＜br＞", "＜/li＞".
 */
const TAG_LIKE_ARTIFACT_PATTERN = /[<＜]\/?[a-zA-Z][a-zA-Z0-9]*[>＞]/g;
const stripTagLikeArtifacts = (text: string): string =>
  text.replace(TAG_LIKE_ARTIFACT_PATTERN, ' ').replace(/ {2,}/g, ' ');

/**
 * The backend statement generator extracts text from this HTML without
 * decoding HTML entities (a common gap in HTML-to-PDF text extraction), so a
 * literal "&", "<", ">", or non-breaking space in a user's narrative — a law
 * firm name like "Smith & Jones", or rich text copied from a rendered page
 * (which often carries non-breaking spaces for layout/formatting) — would
 * normally have to be serialized as an entity
 * ("&amp;"/"&lt;"/"&gt;"/"&nbsp;") to remain valid HTML, and the backend then
 * renders that entity source text verbatim instead of the character it
 * represents. A non-breaking space looks identical to a normal space in the
 * editor, so a user has no way to even notice it's there before it turns
 * into the literal text "&nbsp;" in the generated statement.
 *
 * These four are the *only* characters the HTML serialization spec escapes
 * in text content (see the HTML Living Standard's fragment serialization
 * algorithm's "escapingString" steps) — anything else a user types passes
 * through untouched and never becomes an entity in the first place.
 *
 * Substituting plain-ASCII lookalikes avoids the problem at the source: these
 * are ordinary text characters, not HTML-significant ones, so they never need
 * entity-escaping. Stick to printable ASCII (not e.g. fullwidth Unicode
 * lookalikes) — the generated PDF's font is outside this repo's control, and
 * only ASCII is guaranteed to have glyphs in it; anything else risks silently
 * rendering as tofu/placeholder boxes instead.
 *
 * NON_BREAKING_SPACE is written as an explicit \u00A0 escape rather than a
 * literal character — a literal non-breaking space pasted into source code
 * is visually indistinguishable from a regular space, which makes the code
 * impossible to verify just by reading it.
 */
const NON_BREAKING_SPACE = '\u00A0';
const BACKEND_UNSAFE_CHAR_REPLACEMENTS: Record<string, string> = {
  '&': 'and',
  '<': '[',
  '>': ']',
  [NON_BREAKING_SPACE]: ' ',
};
const BACKEND_UNSAFE_CHAR_PATTERN = new RegExp(`[&<>${NON_BREAKING_SPACE}]`, 'g');
const makeBackendSafe = (text: string): string =>
  text.replace(BACKEND_UNSAFE_CHAR_PATTERN, (char) => BACKEND_UNSAFE_CHAR_REPLACEMENTS[char]);

const sanitizeTextContent = (text: string): string =>
  makeBackendSafe(stripTagLikeArtifacts(text));

// ── Structural cleanup ────────────────────────────────────────────────────

const isEmptyParagraph = (node: ChildNode | null): node is Element =>
  !!node &&
  node.nodeType === Node.ELEMENT_NODE &&
  (node as Element).tagName.toLowerCase() === 'p' &&
  !(node as Element).textContent?.trim();

/**
 * Drop leading/trailing empty `<p>` elements — the boundary artifacts a paste
 * typically leaves behind (e.g. merging into the field's existing default
 * paragraph) rather than intentional content. Interior empty paragraphs are
 * left alone: those are legitimate blank-line spacing the user placed on
 * purpose and must still show up in the preview/generated statement.
 */
const trimBoundaryEmptyParagraphs = (container: Element): void => {
  while (isEmptyParagraph(container.firstChild)) {
    container.removeChild(container.firstChild as ChildNode);
  }
  while (isEmptyParagraph(container.lastChild)) {
    container.removeChild(container.lastChild as ChildNode);
  }
};

/**
 * `stripTagLikeArtifacts` replaces a removed tag with a space so words on
 * either side of it don't run together (e.g. "your<p>matter" -> "your
 * matter"). When the artifact sat at the very start/end of a block element,
 * that space has nothing on one side and becomes a stray leading/trailing
 * space instead. Trim it — a block's own boundary whitespace is never
 * meaningful, unlike whitespace between/around inline tags, which this only
 * touches when the element itself is block-level.
 */
const trimBlockBoundaryWhitespace = (element: Element): void => {
  const { firstChild, lastChild } = element;
  if (firstChild?.nodeType === Node.TEXT_NODE) {
    firstChild.nodeValue = (firstChild.nodeValue || '').replace(/^ +/, '');
  }
  if (lastChild?.nodeType === Node.TEXT_NODE) {
    lastChild.nodeValue = (lastChild.nodeValue || '').replace(/ +$/, '');
  }
};

/**
 * Sanitize editor HTML down to the supported whitelist. Disallowed elements
 * are replaced by their children (text preserved). Returns `<p></p>` when
 * empty.
 *
 * Do not pre-decode entities before parsing. DOMParser already decodes
 * entities as part of normal HTML parsing, and it does so without ever
 * promoting decoded text back into real elements. Pre-decoding (e.g. via a
 * textarea round-trip, as this function used to do) breaks that guarantee:
 * text a user typed or pasted literally — "<p>Hello</p>" as plain characters,
 * correctly serialized by editor.getHTML() as
 * "<p>&lt;p&gt;Hello&lt;/p&gt;</p>" — would get decoded back into a literal
 * "<p>Hello</p>" substring and then parsed as a real, separate paragraph.
 * That silently turned visible text into new paragraph breaks that never
 * appeared in the text box (sc-46148).
 */
export const sanitizeEditorHtml = (html = ''): string => {
  const parser = new DOMParser();
  const inputDoc = parser.parseFromString(html || '<p></p>', 'text/html');
  const outputDoc = document.implementation.createHTMLDocument('');

  const sanitizeNode = (node: Node): Node => {
    if (node.nodeType === Node.TEXT_NODE) {
      return outputDoc.createTextNode(sanitizeTextContent(node.nodeValue || ''));
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return outputDoc.createDocumentFragment();
    }

    const element = node as Element;
    const sourceTag = element.tagName.toLowerCase();
    const normalizedTag = TAG_ALIASES[sourceTag] || sourceTag;

    if (!ALLOWED_TAGS.has(normalizedTag)) {
      const fragment = outputDoc.createDocumentFragment();
      [...element.childNodes].forEach((childNode) => {
        fragment.appendChild(sanitizeNode(childNode));
      });
      return fragment;
    }

    const cleanElement = outputDoc.createElement(normalizedTag);
    [...element.childNodes].forEach((childNode) => {
      cleanElement.appendChild(sanitizeNode(childNode));
    });
    if (BLOCK_TAGS.has(normalizedTag)) {
      trimBlockBoundaryWhitespace(cleanElement);
    }
    return cleanElement;
  };

  const container = outputDoc.createElement('div');
  [...inputDoc.body.childNodes].forEach((node) => {
    container.appendChild(sanitizeNode(node));
  });

  trimBoundaryEmptyParagraphs(container);

  return container.innerHTML.trim() || '<p></p>';
};

/**
 * Escape text for interpolation into HTML. Covers the three characters the HTML
 * serialization spec escapes in text content, plus both quote styles so the result stays
 * safe if it ever lands in an attribute rather than element content.
 *
 * Shared deliberately: this is the only HTML escape in the codebase, and a second copy
 * would be the one that falls behind. Also used by the disbursement email composer
 * (`payabliDisbursementEmail.ts`), whose output is sent verbatim as an email body.
 */
export const escapeHtmlText = (value = ''): string => value
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

/** Convert plain text (with newlines) into a single sanitized `<p>` block. */
export const plainTextToEditorHtml = (value = ''): string => {
  if (!value) return '<p></p>';
  return `<p>${escapeHtmlText(value).replaceAll('\n', '<br/>')}</p>`;
};

/** Normalize a stored value (HTML or plain text) into safe editor HTML. */
export const normalizeEditorValue = (value = ''): string => {
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(value);
  return looksLikeHtml ? sanitizeEditorHtml(value) : plainTextToEditorHtml(value);
};

/** True when the value has no visible text once sanitized. */
export const isEmptyHtml = (value = ''): boolean =>
  !value || sanitizeEditorHtml(value).replace(/<[^>]*>/g, '').trim() === '';
